import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Banknote, CheckCircle2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PaymentForm } from './PaymentForm'
import { PosOrderRefunds } from './PosOrderRefunds'
import { collectPosPayment, fetchPosPayment, posOrderingError } from './service'
import { posEntryGeneration } from './recovery'
import { formatMoney, posErrorText } from './ItemConfiguration'
import type { PosPaymentInput, PosPayment } from './types'

export function PosOrderPayment({ restaurantId, orderId, cancelled, initialOpen = false, initialMethod = 'cash', onClose, onPaid }: {
  restaurantId: number; orderId: number; cancelled: boolean; initialOpen?: boolean
  initialMethod?: 'cash' | 'external_card'; onClose?: () => void; onPaid?: (payment: PosPayment) => void
}) {
  const { t, i18n } = useTranslation('posOrdering')
  // Uses the same recovery prefix cleared by logout; includes both tenant and order.
  const storageKey = `pos-entry:payment:${restaurantId}:${orderId}`
  const generation = useRef(posEntryGeneration()).current
  const [pending, setPending] = useState<PosPaymentInput | null>(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') as PosPaymentInput | null } catch { return null }
  })
  const [open, setOpen] = useState(initialOpen)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const locked = useRef(false)
  const mounted = useRef(true)
  const client = useQueryClient()
  const summary = useQuery({ queryKey: ['pos-payment', restaurantId, orderId],
    queryFn: ({ signal }) => fetchPosPayment(restaurantId, orderId, signal), retry: false, staleTime: 0 })
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  async function collect(input: PosPaymentInput) {
    if (locked.current || generation !== posEntryGeneration()) return
    locked.current = true; setBusy(true); setError('')
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(input))
    } catch {
      setError('recovery_storage_unavailable'); locked.current = false; setBusy(false); return
    }
    setPending(input); setOpen(false)
    try {
      const paid = await collectPosPayment(restaurantId, orderId, input)
      if (generation !== posEntryGeneration()) return
      sessionStorage.removeItem(storageKey)
      if (mounted.current) setPending(null)
      onPaid?.(paid)
      await Promise.all([client.invalidateQueries({ queryKey: ['orders', restaurantId] }),
        client.invalidateQueries({ queryKey: ['pos-cash', restaurantId] }), summary.refetch()])
    } catch (failure) {
      if (generation !== posEntryGeneration()) return
      const problem = posOrderingError(failure)
      const uncertain = !problem.status || problem.status >= 500
      if (!uncertain) {
        try { sessionStorage.removeItem(storageKey) } catch { /* A retained key can safely be retried. */ }
        if (mounted.current) setPending(null)
        void summary.refetch()
      }
      if (mounted.current) setError(uncertain ? 'uncertain_payment' : problem.code)
    } finally { locked.current = false; if (mounted.current) setBusy(false) }
  }
  const paid = summary.data?.payment
  const money = (cents: number) => formatMoney(i18n.language, cents / 100, 'EUR')
  return (
    <section className="space-y-2 border-y py-3" aria-label={t('payment.title')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">{t('payment.title')}</h3>
        {paid ? <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 [.pos-theme_&]:text-emerald-400"><CheckCircle2 aria-hidden className="h-4 w-4" />
          {paid.pendingRefundCents ? t('refund.pending') : paid.refundedCents === paid.amountCents ? t('refund.status.confirmed') : paid.refundedCents ? t('refund.partial') : t('payment.paidMethod', { method: t(`payment.${paid.method}`) })}</span>
          : !open && summary.data && <span className="text-sm font-medium tabular-nums">{t('payment.openAmount', { amount: money(summary.data.remainingCents) })}</span>}
      </div>
      {paid && <p className="text-sm text-muted-foreground">
        {t('payment.recordedAt', { time: new Date(paid.recordedAt).toLocaleString(i18n.language) })}
        {paid.method === 'cash' && ` · ${t('payment.cashSummary', { tendered: money(paid.tenderedCents), change: money(paid.changeCents) })}`}
      </p>}
      {summary.isPending && <p className="text-sm text-muted-foreground">{t('payment.loading')}</p>}
      {summary.isError && <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
        <span>{posErrorText(t, posOrderingError(summary.error).code)}</span>
        <Button variant="outline" className="h-11" onClick={() => { void summary.refetch() }}><RefreshCw aria-hidden />{t('actions.retry')}</Button>
      </div>}
      {error && <p role="alert" className="text-sm text-destructive">{posErrorText(t, error)}</p>}
      {pending ? <div className="space-y-2">
        <p className="text-sm text-status-warning">{t('payment.retryHint')}</p>
        <Button className="h-12 w-full" disabled={busy} onClick={() => { void collect(pending) }}><RefreshCw aria-hidden />{t('payment.reconcile')}</Button>
      </div> : !open && !paid && !cancelled && summary.data && summary.data.remainingCents > 0 && (
        <Button className="h-12 w-full" disabled={busy || summary.isFetching} onClick={() => setOpen(true)}><Banknote aria-hidden />{t('payment.checkout')}</Button>
      )}
      {paid && <PosOrderRefunds restaurantId={restaurantId} orderId={orderId} />}
      {!open && <p className="text-xs text-muted-foreground">{t('payment.pilotNotice')}</p>}
      {open && !paid && !pending && <div className="space-y-3 pt-2">
        <Button variant="ghost" className="h-11" disabled={busy} onClick={() => { setOpen(false); onClose?.() }}>{t('payment.backToOrder')}</Button>
        {summary.data && <PaymentForm totalCents={summary.data.remainingCents} disabled={cancelled || summary.isFetching} busy={busy}
          initialMethod={initialMethod} onConfirm={input => { void collect(input) }} />}
      </div>}

    </section>
  )
}
